import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Alert, Button, Grid, List, message, Skeleton, Space, Steps, Typography } from 'antd';

import { useAIBuilderApi } from '../../api/aibuilder/aibuilder';
import { useProjectApi } from '../../api/projects/projects';
import { TAIBuilderConfig, TDataset, TMLModel } from '../../models/aibuilder/aibuilder';
import { TProject } from '../../models/project/project';
import { useImageModel } from './engine/useImageModel';
import { useTextModel } from './engine/useTextModel';
import { buildTextTrainingSamples } from './engine/buildTextTrainingSamples';
import DatasetStation from './stations/DatasetStation';
import TrainStation from './stations/TrainStation';
import TestStation from './stations/TestStation';
import PurposeStation from './stations/PurposeStation';
import ImproveStation from './stations/ImproveStation';
import DeliverStation from './stations/DeliverStation';
import { AI_BUILDER_PASS_MARK } from './stations/DeliverStation';
import { buildTrainingSamples } from './engine/buildTrainingSamples';
import AIBuilderChatBubble from './chat/AIBuilderChatBubble';
import AIBuilderTheme from './chat/AIBuilderTheme';

interface AIBuilderProjectProps {
  project: TProject;
  enableEditCode: boolean;
  onReadinessChange?: (ready: boolean) => void;
}

export interface AIBuilderProjectRef {
  save: () => Promise<void>;
}

const steps = [
  { title: 'Créer le dataset' },
  { title: 'Configurer / intention' },
  { title: 'Entraîner' },
  { title: 'Tester' },
  { title: 'Améliorer' },
  { title: 'Livrer' },
];
const TEST_STEP_INDEX = 3; // « Tester » rend sa propre conversation.

interface DatasetChoiceProps {
  datasets: TDataset[];
  busy: boolean;
  onCreateNew: (dataType: 'image' | 'text') => void;
  onReuse: (dataset: TDataset) => void;
}

const DatasetChoice: React.FC<DatasetChoiceProps> = ({ datasets, busy, onCreateNew, onReuse }) => (
  <Space direction="vertical" style={{ width: '100%' }}>
    {datasets.length > 0 && (
      <>
        <Typography.Text>
          Tu as déjà {datasets.length > 1 ? 'des datasets' : 'un dataset'}. Réutiliser un dataset
          existant garde ses données et labels ; créer un nouveau dataset part de zéro.
        </Typography.Text>
        <List
          dataSource={datasets}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button key="reuse" onClick={() => onReuse(item)} disabled={busy}>
                  Réutiliser
                </Button>,
              ]}
            >
              {item.name}
            </List.Item>
          )}
        />
      </>
    )}
    <Typography.Text>Ou crée un nouveau dataset :</Typography.Text>
    <Space wrap>
      <Button type="primary" onClick={() => onCreateNew('image')} disabled={busy}>
        Dataset d'images
      </Button>
      <Button onClick={() => onCreateNew('text')} disabled={busy}>
        Dataset de texte
      </Button>
    </Space>
  </Space>
);

const AIBuilderProject = forwardRef<AIBuilderProjectRef, AIBuilderProjectProps>(
  ({ project, enableEditCode, onReadinessChange }, ref) => {
    const { createDataset, getDatasetById, getDatasetsByUserAppId } = useAIBuilderApi();
    const { updateProject } = useProjectApi();
    const imageModel = useImageModel();
    const textModel = useTextModel();
    const { status: imageModelStatus, train: trainImageModel } = imageModel;
    const { status: textModelStatus, train: trainTextModel } = textModel;
    const screens = Grid.useBreakpoint();
    const config = (project.config || {}) as TAIBuilderConfig;
    const datasetId = config.dataset_id;
    const [current, setCurrent] = useState(0);
    const [dataset, setDataset] = useState<TDataset | null>(null);
    const [purpose, setPurpose] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [bootstrapping, setBootstrapping] = useState(!datasetId);
    const [bootstrapError, setBootstrapError] = useState(false);
    const [existingDatasets, setExistingDatasets] = useState<TDataset[] | null>(null);
    const [actionPending, setActionPending] = useState(false);

    useEffect(() => {
      if (!dataset?.models?.length) return;
      const latest = [...dataset.models].sort((a, b) => b.version - a.version)[0];
      if (latest.purpose) setPurpose((current) => current || latest.purpose!);
    }, [dataset]);

    const latestAccuracy = dataset?.models?.length
      ? [...dataset.models].sort((a, b) => b.version - a.version)[0]?.accuracy ?? null
      : null;
    const canSubmit = latestAccuracy != null && latestAccuracy >= AI_BUILDER_PASS_MARK;

    useEffect(() => {
      onReadinessChange?.(canSubmit);
    }, [canSubmit, onReadinessChange]);

    useEffect(() => {
      if (enableEditCode || !dataset) return;
      if (dataset.data_type === 'text') {
        if (textModelStatus !== 'idle') return;
        const samples = buildTextTrainingSamples(dataset);
        if (samples.length === 0) return;
        trainTextModel(samples).catch(() => {});
      } else {
        if (imageModelStatus !== 'idle') return;
        const samples = buildTrainingSamples(dataset);
        if (samples.length === 0) return;
        trainImageModel(samples).catch(() => {});
      }
    }, [enableEditCode, dataset, imageModelStatus, textModelStatus, trainImageModel, trainTextModel]);

    // Les hooks API retournent de nouvelles fonctions à chaque rendu.
    const getDatasetRef = useRef(getDatasetById);
    useEffect(() => {
      getDatasetRef.current = getDatasetById;
    }, [getDatasetById]);

    const getDatasetsByUserAppIdRef = useRef(getDatasetsByUserAppId);
    useEffect(() => {
      getDatasetsByUserAppIdRef.current = getDatasetsByUserAppId;
    }, [getDatasetsByUserAppId]);

    const createDatasetRef = useRef(createDataset);
    useEffect(() => {
      createDatasetRef.current = createDataset;
    }, [createDataset]);

    const updateProjectRef = useRef(updateProject);
    useEffect(() => {
      updateProjectRef.current = updateProject;
    }, [updateProject]);

    const projectRef = useRef(project);
    useEffect(() => {
      projectRef.current = project;
    }, [project]);

    // Garder le dataset créé si seul l'enregistrement du projet doit être retenté.
    const bootstrapRef = useRef<{
      projectId: number;
      dataset?: TDataset;
      saved: boolean;
      inFlight?: Promise<void>;
    }>({ projectId: project.id, saved: false });
    if (bootstrapRef.current.projectId !== project.id) {
      bootstrapRef.current = { projectId: project.id, saved: false };
    }

    useEffect(() => {
      setCurrent(0);
    }, [project.id]);

    useEffect(() => {
      let cancelled = false;
      setDataset(null);
      setLoadError(false);
      setLoading(!!datasetId);
      if (datasetId) {
        getDatasetRef.current(datasetId)
          .then(response => {
            if (!cancelled) setDataset(response.data);
          })
          .catch(() => {
            if (!cancelled) setLoadError(true);
          })
          .finally(() => {
            if (!cancelled) setLoading(false);
          });
      }
      return () => {
        cancelled = true;
      };
    }, [project.id, datasetId]);

    const ensureDataset = useCallback(async (dataType: 'image' | 'text' = 'image'): Promise<void> => {
      const bootstrap = bootstrapRef.current;
      if (datasetId || bootstrap.saved) return;
      if (bootstrap.inFlight) return bootstrap.inFlight;

      const persist = async (): Promise<void> => {
        try {
          if (!bootstrap.dataset) {
            const response = await createDatasetRef.current({
              name: projectRef.current.name || 'AI Builder',
              data_type: dataType,
            });
            bootstrap.dataset = response.data;
          }
          const newConfig = {
            ...(projectRef.current.config || {}),
            dataset_id: bootstrap.dataset.id,
          };
          await updateProjectRef.current(projectRef.current.id, {
            ...projectRef.current,
            config: newConfig,
          });
          projectRef.current = { ...projectRef.current, config: newConfig };
          bootstrap.saved = true;
          if (bootstrapRef.current === bootstrap) {
            setDataset(bootstrap.dataset);
          }
        } catch (error: any) {
          message.error(error?.response?.data ?? error?.message ?? 'Enregistrement impossible.');
          // Ne pas laisser le workflow avancer après un échec.
          throw error;
        }
      };

      bootstrap.inFlight = persist();
      try {
        await bootstrap.inFlight;
      } finally {
        bootstrap.inFlight = undefined;
      }
    }, [datasetId]);

    const handleCreateNew = (dataType: 'image' | 'text') => {
      setExistingDatasets(null);
      setBootstrapping(true);
      ensureDataset(dataType)
        .catch(() => setBootstrapError(true))
        .finally(() => setBootstrapping(false));
    };

    const handleReuse = async (existing: TDataset) => {
      setActionPending(true);
      try {
        const newConfig = {
          ...(projectRef.current.config || {}),
          dataset_id: existing.id,
        };
        await updateProjectRef.current(projectRef.current.id, {
          ...projectRef.current,
          config: newConfig,
        });
        projectRef.current = { ...projectRef.current, config: newConfig };
        bootstrapRef.current.saved = true;
        const response = await getDatasetRef.current(existing.id);
        setExistingDatasets(null);
        setDataset(response.data);
      } catch (error: any) {
        message.error(error?.response?.data ?? error?.message ?? 'Réutilisation impossible.');
      } finally {
        setActionPending(false);
      }
    };

    useEffect(() => {
      if (datasetId) return;
      let cancelled = false;
      setBootstrapping(true);
      setBootstrapError(false);
      getDatasetsByUserAppIdRef.current()
        .then((response) => {
          if (!cancelled) setExistingDatasets(response.data);
        })
        .catch(() => {
          if (!cancelled) setBootstrapError(true);
        })
        .finally(() => {
          if (!cancelled) setBootstrapping(false);
        });
      return () => {
        cancelled = true;
      };
    }, [project.id, datasetId]);

    const save = async (): Promise<void> => {
      await ensureDataset();
    };

    useImperativeHandle(ref, () => ({ save }));

    const reloadDataset = async (createdModel?: TMLModel) => {
      const id = datasetId || dataset?.id;
      if (!id) return;
      const response = await getDatasetRef.current(id);
      setDataset(response.data);
      if (!createdModel) return;
      try {
        const newConfig = {
          ...(projectRef.current.config || {}),
          active_model_id: createdModel.id,
        };
        await updateProjectRef.current(projectRef.current.id, {
          ...projectRef.current,
          config: newConfig,
        });
        projectRef.current = { ...projectRef.current, config: newConfig };
      } catch (error) {
        // Le pointeur actif est secondaire : ne pas bloquer l'affichage du nouveau modèle.
      }
    };

    const pendingDatasetPanel = bootstrapError ? (
        <Alert
          type="error"
          showIcon
          message="Impossible de préparer le dataset."
          description="Rechargez la page pour réessayer."
        />
      ) : (
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      );

    const renderStep = () => {

      if (current === 0) {
        if (dataset) return <DatasetStation dataset={dataset} onChanged={reloadDataset} editable={enableEditCode} />;
        if (existingDatasets) {
          return (
            <DatasetChoice
              datasets={existingDatasets}
              busy={actionPending || bootstrapping}
              onCreateNew={handleCreateNew}
              onReuse={handleReuse}
            />
          );
        }
        return pendingDatasetPanel;
      }
      if (current === 1) {
        return dataset ? (
          <PurposeStation
            dataset={dataset}
            purpose={purpose}
            onChange={setPurpose}
            onDatasetChanged={reloadDataset}
            editable={enableEditCode}
          />
        ) : pendingDatasetPanel;
      }
      if (current === 2) {
        return dataset ? (
          <TrainStation dataset={dataset} imageModel={imageModel} textModel={textModel} purpose={purpose} onChanged={reloadDataset} editable={enableEditCode} />
        ) : pendingDatasetPanel;
      }
      if (current === 3) {
        return dataset ? (
          <TestStation
            dataset={dataset}
            imageModel={imageModel}
            textModel={textModel}
            editable={enableEditCode}
          />
        ) : pendingDatasetPanel;
      }
      if (current === 4) {
        return dataset ? (
          <ImproveStation dataset={dataset} imageModel={imageModel} textModel={textModel} purpose={purpose} onChanged={reloadDataset} editable={enableEditCode} />
        ) : pendingDatasetPanel;
      }
      return dataset ? <DeliverStation dataset={dataset} purpose={purpose} /> : pendingDatasetPanel;
    };

    if (!enableEditCode) {
      return (
        <AIBuilderTheme>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {loading && <Skeleton active paragraph={{ rows: 1 }} title={false} />}
          {loadError && (
            <Alert
              type="error"
              showIcon
              message="Impossible de charger le dataset."
              description="Rechargez la page pour réessayer."
            />
          )}
          {dataset && <Typography.Text>Dataset : {dataset.name}</Typography.Text>}
          <div aria-label="Tester" aria-busy={bootstrapping}>
            <Typography.Title level={5} style={{ marginTop: 0 }}>
              Tester
            </Typography.Title>
            {dataset ? (
              <TestStation dataset={dataset} imageModel={imageModel} textModel={textModel} editable={false} />
            ) : (
              pendingDatasetPanel
            )}
          </div>
          </Space>
        </AIBuilderTheme>
      );
    }

    return (
      <AIBuilderTheme>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Steps
          current={current}
          onChange={setCurrent}
          items={steps}
          size="small"
          direction={screens.lg ? 'horizontal' : 'vertical'}
        />
        {loading && <Skeleton active paragraph={{ rows: 1 }} title={false} />}
        {loadError && (
          <Alert
            type="error"
            showIcon
            message="Impossible de charger le dataset."
            description="Rechargez la page pour réessayer."
          />
        )}
        {dataset && <Typography.Text>Dataset : {dataset.name}</Typography.Text>}
        <div aria-label={steps[current].title} aria-busy={bootstrapping}>
          {current === TEST_STEP_INDEX ? (
            renderStep()
          ) : (
            <AIBuilderChatBubble
              role="bot"
              content={
                <>
                  <Typography.Title level={5} style={{ marginTop: 0 }}>
                    {steps[current].title}
                  </Typography.Title>
                  {renderStep()}
                </>
              }
            />
          )}
        </div>
        <Space style={{ marginTop: 16, justifyContent: 'flex-end', width: '100%' }}>
          <Button disabled={current === 0} onClick={() => setCurrent((c) => c - 1)}>
            Précédent
          </Button>
          <Button
            type="primary"
            disabled={current === steps.length - 1}
            onClick={() => setCurrent((c) => c + 1)}
          >
            Suivant
          </Button>
        </Space>
        </Space>
      </AIBuilderTheme>
    );
  }
);

AIBuilderProject.displayName = 'AIBuilderProject';

export default AIBuilderProject;
